/**
 * ConnectionMetrics - Real-time connection quality indicators
 *
 * Displays:
 * - Compact view: Internet: 🟢 Strong  Audio: 🟢 Clean  ⓘ
 * - Expanded view: Detailed metrics (RTT, packet loss, jitter, etc.)
 *
 * Two modes:
 * - Guest mode: Simple, calming indicators
 * - Host mode: More detailed metrics and controls
 */

import { useState, useCallback } from 'react';
import { useConnectionMetrics } from '../hooks/useConnectionMetrics';
import type { QualityLevel, ConnectionMetrics as ConnectionMetricsType, AudioMetrics } from '../lib/metricsService';
import './ConnectionMetrics.css';

// ============================================================================
// Types
// ============================================================================

interface ConnectionMetricsProps {
  /** Audio stream for monitoring */
  audioStream: MediaStream | null;
  /** RTCPeerConnection for WebRTC stats */
  peerConnection?: RTCPeerConnection | null;
  /** Whether user is the host (shows more details) */
  isHost?: boolean;
  /** Compact mode - minimal UI */
  compact?: boolean;
  /** Show audio meter */
  showAudioMeter?: boolean;
  /** Custom class name */
  className?: string;
}

// ============================================================================
// Sub-components
// ============================================================================

/** Quality indicator dot */
function QualityDot({ level }: { level: QualityLevel }) {
  const colorClass = {
    good: 'quality-good',
    fair: 'quality-fair',
    poor: 'quality-poor',
    unknown: 'quality-unknown',
  }[level];

  return <span className={`quality-dot ${colorClass}`} />;
}

/** Audio level meter */
function AudioMeter({ level, isClipping }: { level: number; isClipping: boolean }) {
  return (
    <div className={`audio-meter ${isClipping ? 'clipping' : ''}`}>
      <div
        className="audio-meter-fill"
        style={{ width: `${Math.min(level, 100)}%` }}
      />
    </div>
  );
}

/** Expanded metrics panel */
function MetricsPanel({
  connection,
  audio,
  onClose,
}: {
  connection: ConnectionMetricsType | null;
  audio: AudioMetrics | null;
  onClose: () => void;
}) {
  return (
    <div className="metrics-panel">
      <div className="metrics-panel-header">
        <span>Connection Details</span>
        <button className="metrics-close-btn" onClick={onClose}>×</button>
      </div>

      <div className="metrics-panel-content">
        {/* Connection Section */}
        <div className="metrics-section">
          <h4>Network</h4>
          <div className="metrics-grid">
            <div className="metric-item">
              <span className="metric-label">Latency (RTT)</span>
              <span className={`metric-value ${getMetricClass(connection?.rtt || 0, 150, 400)}`}>
                {connection?.rtt || 0} ms
              </span>
            </div>
            <div className="metric-item">
              <span className="metric-label">Packet Loss</span>
              <span className={`metric-value ${getMetricClass(connection?.packetLoss || 0, 1, 5)}`}>
                {connection?.packetLoss || 0}%
              </span>
            </div>
            <div className="metric-item">
              <span className="metric-label">Jitter</span>
              <span className={`metric-value ${getMetricClass(connection?.jitter || 0, 20, 50)}`}>
                {connection?.jitter || 0} ms
              </span>
            </div>
            <div className="metric-item">
              <span className="metric-label">Bandwidth</span>
              <span className="metric-value">
                {formatBandwidth(connection?.availableBandwidth || 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Audio Section */}
        <div className="metrics-section">
          <h4>Audio</h4>
          <div className="metrics-grid">
            <div className="metric-item">
              <span className="metric-label">Input Level</span>
              <span className="metric-value">
                {audio?.inputLevel || 0}%
              </span>
            </div>
            <div className="metric-item">
              <span className="metric-label">Peak</span>
              <span className={`metric-value ${audio?.isClipping ? 'metric-poor' : ''}`}>
                {audio?.peakLevel || 0}%
              </span>
            </div>
            <div className="metric-item">
              <span className="metric-label">Noise Floor</span>
              <span className="metric-value">
                {audio?.noiseFloor || 0}%
              </span>
            </div>
            <div className="metric-item">
              <span className="metric-label">Status</span>
              <span className={`metric-value ${getAudioStatusClass(audio)}`}>
                {audio?.qualityLabel || 'Unknown'}
              </span>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="metrics-legend">
          <span className="legend-item"><span className="legend-dot good" /> Good</span>
          <span className="legend-item"><span className="legend-dot fair" /> Fair</span>
          <span className="legend-item"><span className="legend-dot poor" /> Poor</span>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Helper Functions
// ============================================================================

function getMetricClass(value: number, goodThreshold: number, fairThreshold: number): string {
  if (value <= goodThreshold) return 'metric-good';
  if (value <= fairThreshold) return 'metric-fair';
  return 'metric-poor';
}

function getAudioStatusClass(audio: AudioMetrics | null): string {
  if (!audio) return '';
  if (audio.isClipping) return 'metric-poor';
  if (audio.isSilent) return 'metric-fair';
  if (audio.quality === 'good') return 'metric-good';
  return '';
}

function formatBandwidth(kbps: number): string {
  if (kbps >= 1000) {
    return `${(kbps / 1000).toFixed(1)} Mbps`;
  }
  return `${Math.round(kbps)} kbps`;
}

// ============================================================================
// Main Component
// ============================================================================

export function ConnectionMetrics({
  audioStream,
  peerConnection = null,
  isHost = false,
  compact = false,
  showAudioMeter = false,
  className = '',
}: ConnectionMetricsProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    connectionStatus,
    audioStatus,
    connection,
    audio,
    isConnected,
  } = useConnectionMetrics({
    audioStream,
    peerConnection,
    pollingInterval: isExpanded ? 500 : 1000, // Faster updates when expanded
  });

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  // Guest view - very simple
  if (!isHost && !isExpanded) {
    return (
      <div className={`connection-metrics guest ${className}`}>
        <div className="metrics-compact">
          <span className="metric-indicator">
            <QualityDot level={connectionStatus.level} />
            <span className="metric-text">
              {isConnected ? connectionStatus.label : 'Connecting...'}
            </span>
          </span>
        </div>
      </div>
    );
  }

  // Compact host view
  if (compact) {
    return (
      <div className={`connection-metrics compact ${className}`}>
        <div className="metrics-compact">
          <span className="metric-indicator">
            <span className="metric-label-small">Internet:</span>
            <QualityDot level={connectionStatus.level} />
            <span className="metric-text">{connectionStatus.label}</span>
          </span>
          <span className="metric-separator">|</span>
          <span className="metric-indicator">
            <span className="metric-label-small">Audio:</span>
            <QualityDot level={audioStatus.level} />
            <span className="metric-text">{audioStatus.label}</span>
          </span>
          <button
            className="metrics-expand-btn"
            onClick={toggleExpanded}
            title="Show detailed metrics"
          >
            ⓘ
          </button>
        </div>

        {isExpanded && (
          <MetricsPanel
            connection={connection}
            audio={audio}
            onClose={() => setIsExpanded(false)}
          />
        )}
      </div>
    );
  }

  // Full host view
  return (
    <div className={`connection-metrics full ${className}`}>
      <div className="metrics-row">
        {/* Connection indicator */}
        <div className="metric-card">
          <div className="metric-card-header">
            <QualityDot level={connectionStatus.level} />
            <span className="metric-card-title">Internet</span>
          </div>
          <span className="metric-card-value">{connectionStatus.label}</span>
          {isHost && connection && (
            <span className="metric-card-detail">
              {connection.rtt}ms · {connection.packetLoss}% loss
            </span>
          )}
        </div>

        {/* Audio indicator */}
        <div className="metric-card">
          <div className="metric-card-header">
            <QualityDot level={audioStatus.level} />
            <span className="metric-card-title">Audio</span>
          </div>
          <span className="metric-card-value">{audioStatus.label}</span>
          {showAudioMeter && audio && (
            <AudioMeter level={audio.inputLevel} isClipping={audio.isClipping} />
          )}
        </div>

        {/* Expand button */}
        <button
          className={`metrics-expand-btn-large ${isExpanded ? 'active' : ''}`}
          onClick={toggleExpanded}
          title={isExpanded ? 'Hide details' : 'Show details'}
        >
          {isExpanded ? '▲' : '▼'}
        </button>
      </div>

      {/* Expanded panel */}
      {isExpanded && (
        <MetricsPanel
          connection={connection}
          audio={audio}
          onClose={() => setIsExpanded(false)}
        />
      )}
    </div>
  );
}

// ============================================================================
// Compact Indicator Component (for header)
// ============================================================================

export function CompactMetricsIndicator({
  audioStream,
  peerConnection = null,
  isHost: _isHost = false,
  className = '',
}: {
  audioStream: MediaStream | null;
  peerConnection?: RTCPeerConnection | null;
  isHost?: boolean;
  className?: string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    connectionStatus,
    audioStatus,
    connection,
    audio,
    isConnected,
  } = useConnectionMetrics({
    audioStream,
    peerConnection,
  });

  // If not connected yet, show simple connecting state
  if (!isConnected) {
    return (
      <div className={`compact-metrics-indicator connecting ${className}`}>
        <span className="connecting-dot" />
        <span>Connecting...</span>
      </div>
    );
  }

  return (
    <div className={`compact-metrics-indicator ${className}`}>
      <div className="compact-metrics-row">
        {/* Internet status */}
        <span className="compact-metric">
          <span className="compact-label">Internet:</span>
          <QualityDot level={connectionStatus.level} />
          <span className="compact-value">{connectionStatus.label}</span>
        </span>

        {/* Audio status */}
        <span className="compact-metric">
          <span className="compact-label">Audio:</span>
          <QualityDot level={audioStatus.level} />
          <span className="compact-value">{audioStatus.label}</span>
        </span>

        {/* Info button */}
        <button
          className="compact-info-btn"
          onClick={() => setIsExpanded(!isExpanded)}
          title="Connection details"
        >
          ⓘ
        </button>
      </div>

      {/* Expanded panel */}
      {isExpanded && (
        <div className="compact-metrics-dropdown">
          <MetricsPanel
            connection={connection}
            audio={audio}
            onClose={() => setIsExpanded(false)}
          />
        </div>
      )}
    </div>
  );
}

export default ConnectionMetrics;
